import React, { useState, useEffect } from 'react';
import { Shield, AlertCircle } from 'lucide-react';

import { storage } from '../services/storage';
import {
  ADMIN_GOOGLE_STATE_KEY,
  buildGoogleAuthUrl,
  newOAuthState,
} from './admin/auth/googleSignIn';

// The Admin Portal is Google sign-in ONLY (owner decision 2026-09-29): there is no
// email/password form. Who may sign in is decided by the server — a verified
// coachcedar.com Google Workspace account with a pre-created, active Admin Portal
// account — never by a constant here.
const GOOGLE_CLIENT_ID = '788522045775-skiejlpub2eah0bpl3bo71cdl2j222kd.apps.googleusercontent.com';

export const AdminLogin: React.FC = () => {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Load Google Sign-In library
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  
  const handleGoogleSignIn = async () => {
    setError('');
    setIsLoading(true);

    try {
      // A fresh state per attempt, kept on this device; the callback refuses any
      // response that does not carry it back (review of #4227, LOW-2).
      const state = newOAuthState();
      await storage.setItem(ADMIN_GOOGLE_STATE_KEY, state);
      window.location.href = buildGoogleAuthUrl(GOOGLE_CLIENT_ID, window.location.origin, state);
    } catch {
      setIsLoading(false);
      setError('Google sign-in could not start in this browser.');
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(160deg, #0B1614 0%, #113C2B 40%, #1F6C4F 100%)' }}>
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img
              src="/CC_Logo_reverse_top.png"
              alt="Coach Cedar"
              className="h-24 w-auto"
              onError={(e) => {
                // Fallback to the new side-by-side white lockup
                (e.target as HTMLImageElement).src = '/Logo-white-side.svg';
              }}
            />
          </div>
          <h1 className="text-xl font-semibold text-white mt-3" style={{ fontFamily: "'League Spartan', sans-serif" }}>Admin Portal</h1>
        </div>
        
        {/* Login Card */}
        <div className="bg-white rounded-xl shadow-2xl p-8" style={{ boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
          {/* Google Sign-In Button — the only way in */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ outlineColor: '#1F6C4F' }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {isLoading ? 'Redirecting to Google…' : 'Sign in with Google'}
          </button>

          <p className="mt-4 text-center text-xs text-gray-500">
            Use your coachcedar.com Google account. A super admin must add it first.
          </p>

          {/* Error Message */}
          {error && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Sign-in Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}
          
          {/* Security Notice */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center text-sm text-gray-600">
              <Shield className="h-4 w-4 mr-2" style={{ color: '#1F6C4F' }} />
              <span>All admin actions are logged and monitored</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
